import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaQuoteLeft, FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import theme from '../../theme';

import belenGemuseLogo from '../../assets/images/references/belen_gemuse.png';
import cbGrosshandelLogo from '../../assets/images/references/cbgrosshandel.png';
import dosteliLogo from '../../assets/images/references/dosteli_food.png';
import pasaLogo from '../../assets/images/references/pasa_lebensmittel.png';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Section = styled.section`
  padding: 6rem 0;
  background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 50%, #f8f9fa 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(231, 76, 60, 0.15), transparent);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3.5rem;
  animation: ${fadeIn} 0.6s ease-out;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: linear-gradient(135deg, rgba(231, 76, 60, 0.08), rgba(231, 76, 60, 0.15));
  color: ${theme.colors.primary};
  padding: 0.45rem 1.1rem;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 2px;
  margin-bottom: 1.25rem;
  border: 1px solid rgba(231, 76, 60, 0.12);
`;

const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: ${theme.colors.secondary};
  margin-bottom: 0.75rem;
  line-height: 1.2;

  @media (max-width: ${theme.breakpoints.tablet}) { font-size: 2rem; }
  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.6rem; }
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  color: ${theme.colors.darkGray};
  max-width: 550px;
  margin: 0 auto;
  line-height: 1.6;
`;

const CarouselWrapper = styled.div`
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
`;

const SlideContainer = styled.div`
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 0;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04);
  animation: ${slideIn} 0.5s ease-out;
  min-height: 420px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const LeftPanel = styled.div`
  background: linear-gradient(160deg, ${theme.colors.secondary} 0%, #1a1a2e 100%);
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    bottom: -30px;
    right: -30px;
    width: 120px;
    height: 120px;
    background: radial-gradient(circle, rgba(231, 76, 60, 0.12) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const AuthorBlock = styled.div`
  position: relative;
  z-index: 1;
`;

const AuthorAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${theme.colors.primary}, #c0392b);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 800;
  font-size: 1.1rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 8px 20px rgba(231, 76, 60, 0.3);
`;

const AuthorName = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.3rem;
`;

const AuthorPosition = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 1.5rem;
  line-height: 1.4;
`;

const MicroProofList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: relative;
  z-index: 1;
`;

const MicroProofItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  animation: ${fadeIn} 0.4s ease-out;
  animation-delay: ${props => props.$delay || '0s'};
  animation-fill-mode: both;
`;

const ProofIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  margin-top: 2px;

  svg {
    font-size: 0.5rem;
    color: white;
  }
`;

const ProofText = styled.span`
  font-size: 0.88rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.35;
`;

const CompanyLogoWrap = styled.div`
  margin-top: 1.5rem;
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 0.5rem 1rem;
  border-radius: 10px;
`;

const CompanyLogo = styled.img`
  height: 36px;
  max-width: 120px;
  object-fit: contain;
`;

const RightPanel = styled.div`
  background: #ffffff;
  padding: 2.5rem 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 2rem 1.5rem;
  }
`;

const QuoteIcon = styled.div`
  color: ${theme.colors.primary};
  font-size: 1.5rem;
  margin-bottom: 1.25rem;
  opacity: 0.2;
`;

const TestimonialText = styled.p`
  font-size: 0.95rem;
  line-height: 1.85;
  color: #4b5563;
  flex: 1;
  overflow-y: auto;
  max-height: 300px;
  padding-right: 0.5rem;
  white-space: pre-line;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    max-height: 250px;
  }
`;

const NavControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 2rem;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
`;

const NavButtons = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const NavBtn = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1.5px solid #e5e7eb;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  color: ${theme.colors.secondary};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  &:hover:not(:disabled) {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    box-shadow: 0 4px 16px rgba(231, 76, 60, 0.12);
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }

  svg { font-size: 0.8rem; }
`;

const ProgressBar = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const ProgressDot = styled.button`
  width: ${props => props.$active ? '32px' : '8px'};
  height: 8px;
  border-radius: 4px;
  border: none;
  background: ${props => props.$active
    ? `linear-gradient(90deg, ${theme.colors.primary}, #ff6b5a)`
    : '#e5e7eb'};
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  ${props => props.$active && css`
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
      background-size: 200% 100%;
      animation: ${shimmer} 2s infinite;
    }
  `}

  &:hover {
    background: ${props => props.$active
      ? `linear-gradient(90deg, ${theme.colors.primary}, #ff6b5a)`
      : '#94a3b8'};
  }
`;

const CTALink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${theme.colors.primary};
  font-size: 0.9rem;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.25s ease;

  &:hover {
    gap: 0.75rem;
    color: #c0392b;
  }

  svg { font-size: 0.75rem; transition: transform 0.25s ease; }
  &:hover svg { transform: translateX(3px); }
`;

const companyLogos = {
  1: belenGemuseLogo,
  2: cbGrosshandelLogo,
  3: dosteliLogo,
  4: pasaLogo
};

const AUTOPLAY_INTERVAL = 8000;

const Testimonials = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const testimonialKeys = [4, 2, 1, 3];
  const total = testimonialKeys.length;

  const goTo = useCallback((idx) => {
    setActiveIndex(idx);
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex(prev => (prev + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    setActiveIndex(prev => (prev - 1 + total) % total);
  }, [total]);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(goNext, AUTOPLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [isPaused, goNext]);

  const currentKey = testimonialKeys[activeIndex];
  const prefix = `references.testimonials.items.testimonial${currentKey}`;

  const getInitials = (name) => name.split(' ').map(n => n[0]).join('').substring(0, 2);

  const microProofItems = t(`${prefix}.microProof`, { returnObjects: true }) || [];

  return (
    <Section>
      <Container>
        <Header>
          <Badge>{t('homeTestimonials.badge')}</Badge>
          <Title>{t('homeTestimonials.title')}</Title>
          <Subtitle>{t('homeTestimonials.subtitle')}</Subtitle>
        </Header>

        <CarouselWrapper
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <SlideContainer key={activeIndex}>
            <LeftPanel>
              <AuthorBlock>
                <AuthorAvatar>
                  {getInitials(t(`${prefix}.author`))}
                </AuthorAvatar>
                <AuthorName>{t(`${prefix}.author`)}</AuthorName>
                <AuthorPosition>
                  {t(`${prefix}.position`)} — {t(`${prefix}.company`)}
                </AuthorPosition>

                <MicroProofList>
                  {Array.isArray(microProofItems) && microProofItems.map((item, idx) => (
                    <MicroProofItem key={idx} $delay={`${idx * 0.1}s`}>
                      <ProofIcon><FaCheck /></ProofIcon>
                      <ProofText>{item}</ProofText>
                    </MicroProofItem>
                  ))}
                </MicroProofList>
              </AuthorBlock>

              <CompanyLogoWrap>
                <CompanyLogo
                  src={companyLogos[currentKey]}
                  alt={t(`${prefix}.company`)}
                  loading="lazy"
                />
              </CompanyLogoWrap>
            </LeftPanel>

            <RightPanel>
              <QuoteIcon><FaQuoteLeft /></QuoteIcon>
              <TestimonialText>
                {t(`${prefix}.text`)}
              </TestimonialText>
            </RightPanel>
          </SlideContainer>

          <NavControls>
            <NavButtons>
              <NavBtn onClick={goPrev} aria-label="Previous">
                <FaChevronLeft />
              </NavBtn>
              <NavBtn onClick={goNext} aria-label="Next">
                <FaChevronRight />
              </NavBtn>
            </NavButtons>

            <ProgressBar>
              {testimonialKeys.map((_, i) => (
                <ProgressDot
                  key={i}
                  $active={i === activeIndex}
                  onClick={() => goTo(i)}
                />
              ))}
            </ProgressBar>

            <CTALink to={`/${lang}/references`}>
              {t('homeTestimonials.cta')} <FaArrowRight />
            </CTALink>
          </NavControls>
        </CarouselWrapper>
      </Container>
    </Section>
  );
};

export default Testimonials;
